import {
  Control,
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
} from 'react-hook-form';
import { SupportedLocale } from '@/types/supported-locale.type';
import StyledTextarea from './StyledTextarea';
import { useLanguage } from '@/contexts/LanguageContext';
import { useMemo } from 'react';

interface StyledTranslatableTextareaProps<T extends FieldValues = FieldValues> {
  control: Control<T>;
  name: string; // e.g., "longDescription"
  language: SupportedLocale;
  originalName: Path<T>;
  setValue: UseFormSetValue<T>;
  title?: string;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  className?: string;
}

const StyledTranslatableTextarea = <T extends FieldValues = FieldValues>({
  control,
  name,
  language,
  originalName,
  setValue,
  title,
  placeholder,
  disabled,
  rows,
  className,
}: StyledTranslatableTextareaProps<T>) => {
  // Use a computed field name that includes the language
  const translationFieldName = `translations.${language}.${name}`;
  const { currentLanguage } = useLanguage();

  const onChange = useMemo(
    () =>
      language === currentLanguage
        ? (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setValue(
              originalName,
              event.target.value as PathValue<T, Path<T>>,
              {
                shouldDirty: true,
              },
            );
          }
        : undefined,
    [language, currentLanguage, originalName, setValue],
  );

  return (
    <StyledTextarea
      key={`${name}-${language}`}
      control={control}
      name={translationFieldName as Path<T>}
      onChange={onChange}
      title={title}
      placeholder={placeholder}
      disabled={disabled}
      rows={rows}
      className={className}
    />
  );
};

export default StyledTranslatableTextarea;
